import React, { useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { EventEmail } from '../../types/api';
import SendPanel, { AskConfirm } from './SendPanel';

/** Emails sent through their own legacy flow (own endpoint, own rules, own confirmation). */
export const SYSTEM_SENDABLE_KINDS = ['invitation', 'reminder', 'final_confirmation'] as const;
/** All system kinds, including the automatic RSVP emails (never listed for sending). */
export const AUTOMATIC_OR_SYSTEM = ['invitation', 'reminder', 'final_confirmation', 'rsvp_confirmation', 'rsvp_absence'];

export interface SystemEmailAction {
  kind: (typeof SYSTEM_SENDABLE_KINDS)[number];
  label: string;
  description: string;
  /** People the existing flow will target (shown as a hint; the flow's own dialog stays the authority). */
  count: number;
  /** Last recorded send (#56), null/undefined = none recorded. */
  lastSend?: { sent_at: string; sent: number; failed: number } | null;
  /** "N destinataire(s) : <who>" - N computed server-side with the query the send uses (#57). */
  countLabel: string;
  /** Opens the existing confirmation flow, unchanged. */
  onSend: () => void;
}

const when = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '';

/** #57: ONE format for every row: "dernier envoi : <date> · N envoyé(s), M échec(s)" (both numbers, even at 0 failures). */
export const lastSendLine = (at: string, sent: number, failed: number): string =>
  `dernier envoi : ${when(at)} · ${sent} envoyé(s), ${failed} échec(s)`;

/** "Dernier envoi" line of a system email (same text in the picker and in the Envoi emails tab). */
export const systemLastSentText = (s: SystemEmailAction): string =>
  s.lastSend ? lastSendLine(s.lastSend.sent_at, s.lastSend.sent, s.lastSend.failed) : 'aucun envoi enregistré';

export const CustomLastSent: React.FC<{ eventId: number; emailId: number }> = ({ eventId, emailId }) => {
  const q = useQuery({
    queryKey: ['email-sends', eventId, emailId],
    queryFn: () => apiService.getEmailSends(eventId, emailId),
  });
  if (q.isLoading) return <Spinner size="xs" />;
  const last = q.data && q.data[0];
  return (
    <Text fontSize="xs" color="gray.600" data-testid={`last-sent-${emailId}`}>
      {last ? lastSendLine(last.started_at ?? '', last.sent_count, last.failed_count) : 'aucun envoi'}
    </Text>
  );
};

const SendEmailModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  eventId: number;
  systemEmails: SystemEmailAction[];
  /** 'system:<kind>' | 'custom:<id>': open already on that email and skip the picker step. */
  initialPick?: string | null;
}> = ({ isOpen, onClose, eventId, systemEmails, initialPick = null }) => {
  const [pickedState, setPicked] = useState<string | null>(null); // 'system:<kind>' | 'custom:<id>'
  const picked = initialPick ?? pickedState;
  const [confirm, setConfirm] = useState<AskConfirm | null>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const emailsQ = useQuery({
    queryKey: ['event-emails', eventId],
    queryFn: () => apiService.getEventEmails(eventId),
    enabled: isOpen,
  });
  const customEmails: EventEmail[] = (emailsQ.data || []).filter((e) => !AUTOMATIC_OR_SYSTEM.includes(e.kind));

  const close = () => { setPicked(null); setConfirm(null); onClose(); };

  if (!isOpen) return null;

  const pickedSystem = picked?.startsWith('system:') ? systemEmails.find((s) => 'system:' + s.kind === picked) : undefined;
  const pickedCustom = picked?.startsWith('custom:') ? customEmails.find((e) => 'custom:' + e.id === picked) : undefined;

  return (
    <>
      <Modal isOpen={isOpen} onClose={close} size={{ base: 'full', md: 'xl' }} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent mx={{ base: 0, md: 4 }} data-testid="send-email-modal">
          <ModalHeader>Envoyer un e-mail</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={4}>
              {!initialPick && <Text fontSize="sm" fontWeight="semibold">1. Choisir l'e-mail</Text>}
              {!initialPick && (
              <VStack align="stretch" spacing={2}>
                {systemEmails.map((s) => (
                  <Button
                    key={s.kind}
                    variant={picked === 'system:' + s.kind ? 'solid' : 'outline'}
                    colorScheme={picked === 'system:' + s.kind ? 'blue' : 'gray'}
                    h="auto" py={2} px={4} justifyContent="flex-start" textAlign="left"
                    onClick={() => setPicked('system:' + s.kind)}
                    data-testid={`pick-system-${s.kind}`}
                  >
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold">{s.label}</Text>
                      <Text fontSize="xs" fontWeight="normal" data-testid={`last-sent-${s.kind}`}>
                        {systemLastSentText(s)}
                      </Text>
                    </Box>
                  </Button>
                ))}
                {emailsQ.isLoading && <Spinner size="sm" />}
                {customEmails.map((e) => (
                  <Button
                    key={e.id}
                    variant={picked === 'custom:' + e.id ? 'solid' : 'outline'}
                    colorScheme={picked === 'custom:' + e.id ? 'blue' : 'gray'}
                    h="auto" py={2} px={4} justifyContent="flex-start" textAlign="left"
                    onClick={() => setPicked('custom:' + e.id)}
                    data-testid={`pick-custom-${e.id}`}
                  >
                    <Box>
                      <Text fontSize="sm" fontWeight="semibold">{e.name}</Text>
                      <CustomLastSent eventId={eventId} emailId={e.id} />
                    </Box>
                  </Button>
                ))}
              </VStack>
              )}

              {pickedSystem && (
                <Box borderWidth="1px" borderRadius="md" p={4} data-testid="system-panel">
                  <Text fontSize="sm" fontWeight="semibold" mb={1}>{initialPick ? 'Destinataires' : '2. Destinataires'}</Text>
                  <Text fontSize="sm" mb={2}>{pickedSystem.description}</Text>
                  <Badge colorScheme="blue" mb={3} data-testid="system-count">{pickedSystem.countLabel}</Badge>
                  <Box>
                    <Button
                      colorScheme="blue" size="sm"
                      isDisabled={pickedSystem.count === 0}
                      onClick={() => { const go = pickedSystem.onSend; close(); go(); }}
                      data-testid="system-continue"
                    >
                      Continuer
                    </Button>
                  </Box>
                </Box>
              )}

              {pickedCustom && (
                <Box>
                  <Text fontSize="sm" fontWeight="semibold" mb={2}>{initialPick ? 'Destinataires' : '2. Destinataires'}</Text>
                  <SendPanel
                    eventId={eventId}
                    emailId={pickedCustom.id}
                    subject={pickedCustom.subject}
                    dirty={false}
                    askConfirm={setConfirm}
                  />
                </Box>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
      {confirm && (
        <AlertDialog isOpen leastDestructiveRef={cancelRef} onClose={() => setConfirm(null)} isCentered>
          <AlertDialogOverlay>
            <AlertDialogContent mx={4}>
              <AlertDialogHeader fontSize="lg">{confirm.title}</AlertDialogHeader>
              <AlertDialogBody>{confirm.body}</AlertDialogBody>
              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={() => setConfirm(null)} data-testid="send-modal-cancel">Annuler</Button>
                <Button colorScheme="red" ml={3} onClick={() => { confirm.onConfirm(); setConfirm(null); }} data-testid="send-modal-confirm">
                  {confirm.confirmLabel}
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      )}
    </>
  );
};

export default SendEmailModal;